// ExportToExcelSecond.ts
import ExcelJS from 'exceljs'
import { saveAs } from 'file-saver'
import _ from 'lodash'
import moment from 'moment'
import { toBuddhistYear } from 'utils/app.utils'
import { forceTextOnDateCells } from 'utils/excel.utils'

export interface RemunerationSecondReportData {
    branchName: string
    date: string
    t: any
    rowDailyBalance: Array<{
        sumTreatment: number
        sumProduct: number
        sumAll: number | null
    }>
    rowIncome: Array<{
        detail: string
        price: number
        total: number
        paymentChannelId?: number | null
    }>
    rowTreatmentRight: Array<{
        detail: string
        price: number
        total: number
        paymentChannelId?: number | null
    }>
    rowDf: Array<{
        detail: string | null
        price: number
        total: number
    }>
    rowExpenses: Array<{
        expenseTypeName: string
        price: number
        total: number
    }>
}

export async function ExportToExcelSecond(report: RemunerationSecondReportData) {
    const wb = new ExcelJS.Workbook()
    const ws = wb.addWorksheet('ใบส่งเงิน (แบบที่2)', {
        pageSetup: { orientation: 'portrait' }
    })
    ws.columns = []
    ws.columns = [{ width: 40 }, { width: 12, style: { numFmt: '#,##0.00' } }, { width: 12, style: { numFmt: '#,##0.00' } }]

    let rowIndex = 1
    ws.mergeCells(rowIndex, 1, rowIndex, 3)
    ws.getCell(rowIndex, 1).value =
        `${report.t('REPORT.REPORT_NAME.REMITTANCE_SECOND')}`
    ws.getCell(rowIndex, 1).font = { size: 16, bold: true }
    ws.getCell(rowIndex, 1).alignment = { horizontal: 'center' }
    rowIndex++

    ws.mergeCells(rowIndex, 1, rowIndex, 3)
    ws.getCell(rowIndex, 1).value =
        `${report.t('REPORT.TITLE.BRANCH')} : ${report.branchName}    ` +
        `${report.t('REPORT.TITLE.DATE')} : ${toBuddhistYear(moment(report.date), 'DD/MM/YYYY')}`
    ws.getCell(rowIndex, 1).font = { size: 12 }
    ws.getCell(rowIndex, 1).alignment = { horizontal: 'left' }
    rowIndex += 2

    const header = ws.addRow([
        report.t('REPORT.TABLE.REMITTANCE.LIST'),
        report.t('REPORT.TABLE.REMITTANCE.QTY'),
        report.t('REPORT.TABLE.REMITTANCE.ALL_QTY')
    ])
    header.eachCell(cell => {
        cell.font = { bold: true, size: 12 }
        cell.alignment = { horizontal: 'center' }
        cell.border = {
            top: { style: 'thin' }, left: { style: 'thin' },
            bottom: { style: 'thin' }, right: { style: 'thin' }
        }
    })
    rowIndex++

    const bal = report.rowDailyBalance[0] || { sumProduct: 0, sumTreatment: 0, sumAll: 0 }
    ws.addRow([
        report.t('REPORT.TABLE.REMITTANCE.NET_TOOLS'),
        bal.sumProduct,
        '-'
    ])
    ws.addRow([
        report.t('REPORT.TABLE.REMITTANCE.NET_OPERATIVE'),
        bal.sumTreatment,
        '-'
    ])
    const netDaily = bal.sumProduct + bal.sumTreatment
    const netDailyRow = ws.addRow([
        report.t('REPORT.TABLE.REMITTANCE.NET_DAILY'),
        netDaily,
        '-'
    ])
    netDailyRow.font = { bold: false }

    const combinedIncome = [
        ...report.rowIncome,
        ...report.rowTreatmentRight
    ].sort((a, b) => {
        const ai = a.paymentChannelId ?? Number.MAX_SAFE_INTEGER
        const bi = b.paymentChannelId ?? Number.MAX_SAFE_INTEGER
        return ai - bi
    })
    combinedIncome.forEach(it => {
        ws.addRow([
            it.detail,
            it.price ?? 0,
            it.total ?? 0
        ]).eachCell((cell, col) => {
            if (col > 1) {
                cell.numFmt = '#,##0.00'
                cell.alignment = { horizontal: 'right' }
            } else {
                cell.alignment = { horizontal: 'left' }
            }
        })
    })

    report.rowDf
        .filter(it => it.detail)
        .forEach(it => {
            ws.addRow([
                it.detail,
                it.price ?? 0,
                it.total ?? 0
            ]).eachCell((cell, col) => {
                if (col > 1) {
                    cell.numFmt = '#,##0.00'
                    cell.alignment = { horizontal: 'right' }
                } else {
                    cell.alignment = { horizontal: 'left' }
                }
            })
        })

    report.rowExpenses.forEach(it => {
        ws.addRow([
            it.expenseTypeName,
            it.price,
            it.total
        ]).eachCell((cell, col) => {
            if (col > 1) {
                cell.numFmt = '#,##0.00'
                cell.alignment = { horizontal: 'right' }
            } else {
                cell.alignment = { horizontal: 'left' }
            }
        })
    })


    const sum = _.sumBy(report.rowIncome, 'amountPaidCash') - _.sumBy(report.rowExpenses, 'price')
    const summaryRow = ws.addRow([
        report.t('REPORT.TABLE.REMITTANCE.SUM_INCOME_EXPENSES'),
        sum,
        '-'
    ])
    summaryRow.eachCell((cell, col) => {
        cell.font = { bold: false }
        const val = cell.value
        if (col === 1 || val === '-') {
            cell.alignment = { horizontal: 'left' }
        } else {
            cell.alignment = { horizontal: 'right' }
        }
        if (col > 1 && typeof val === 'number') {
            cell.numFmt = '#,##0.00'
        }
    })


    forceTextOnDateCells(wb)
    const buf = await wb.xlsx.writeBuffer()
    saveAs(
        new Blob([buf]),
        `${report.t('REPORT.REPORT_NAME.REMITTANCE_SECOND')}_${moment().format('YYYYMMDD')}.xlsx`
    )
}
